import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { NumberInput, toNumberValue } from '@/components/molecules/NumberInput';
import { Skeleton } from '@/components/atoms/skeleton';
import { Textarea } from '@/components/atoms/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  type ReportSettingsTab,
  useReportSettings,
  useUpdateReportSettings,
} from '@/hooks/reports/useReportSettings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/reports')({
  component: ReportsSettingsPage,
});

// The `fieldWorkflow` wire id predates the doc-editor redo — the tab now
// holds the surviving workflow toggles (multi-tech editing + AI assists).
const TABS: { id: ReportSettingsTab; label: string }[] = [
  { id: 'defaults', label: 'Defaults' },
  { id: 'branding', label: 'Branding' },
  { id: 'fieldWorkflow', label: 'Workflow' },
  { id: 'sharing', label: 'Sharing & Access' },
  { id: 'signatures', label: 'Signatures' },
];

function ReportsSettingsPage() {
  const { data, isLoading } = useReportSettings();
  const [active, setActive] = useState<ReportSettingsTab>('defaults');
  const update = useUpdateReportSettings();
  const settings = data as Record<ReportSettingsTab, Record<string, unknown>> | undefined;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Report Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Defaults, branding, workflow, sharing, and signatures.
        </p>
      </header>

      <div className="flex border-b mb-6 gap-1 items-end">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              active === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
        <Link
          to="/dashboard/settings/reports/templates"
          className="px-4 py-2 text-sm font-medium border-b-2 border-transparent text-muted-foreground hover:text-foreground ml-auto"
        >
          Templates →
        </Link>
      </div>

      {isLoading || !settings ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      ) : (
        <TabPanel
          tab={active}
          settings={settings[active]}
          onSave={(patch) => update.mutate({ tab: active, patch })}
          saving={update.isPending}
        />
      )}
    </div>
  );
}

function TabPanel({
  tab,
  settings,
  onSave,
  saving,
}: {
  tab: ReportSettingsTab;
  settings: Record<string, unknown>;
  onSave: (patch: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>(settings);
  useEffect(() => setDraft(settings), [settings, tab]);

  const update = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="space-y-4">
      {tab === 'defaults' && <DefaultsForm draft={draft} update={update} />}
      {tab === 'branding' && <BrandingForm draft={draft} update={update} />}
      {tab === 'fieldWorkflow' && <WorkflowForm draft={draft} update={update} />}
      {tab === 'sharing' && <SharingForm draft={draft} update={update} />}
      {tab === 'signatures' && <SignaturesForm draft={draft} update={update} />}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={() => onSave(draft)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[220px_1fr] items-center gap-4">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

function DefaultsForm({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  return (
    <>
      <Row label="Number prefix">
        <Input
          value={String(draft.numberPrefix ?? '')}
          onChange={(e) => update('numberPrefix', e.target.value)}
        />
      </Row>
      <Row label="Sequence start">
        <NumberInput
          value={toNumberValue(draft.sequenceStart)}
          allowDecimal={false}
          placeholder="1001"
          onChange={(next) => update('sequenceStart', next)}
        />
      </Row>
      <Row label="Share-link expiry (days)">
        <NumberInput
          value={toNumberValue(draft.shareLinkExpiryDays)}
          allowDecimal={false}
          placeholder="365"
          onChange={(next) => update('shareLinkExpiryDays', next)}
        />
      </Row>
      <Row label="Default access mode">
        <Select
          value={String(draft.accessMode ?? 'PUBLIC_LINK')}
          onValueChange={(v) => update('accessMode', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PUBLIC_LINK">Public link</SelectItem>
            <SelectItem value="EMAIL_GATED">Email gated</SelectItem>
            <SelectItem value="PORTAL_ONLY">Portal only</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Auto-publish on submit">
        <input
          type="checkbox"
          checked={Boolean(draft.autoPublishOnTechSubmit ?? true)}
          onChange={(e) => update('autoPublishOnTechSubmit', e.target.checked)}
        />
      </Row>
    </>
  );
}

function BrandingForm({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  return (
    <>
      <Row label="Primary color">
        <Input
          type="color"
          value={String(draft.primaryColor ?? '#1F2937')}
          onChange={(e) => update('primaryColor', e.target.value)}
          className="w-20"
        />
      </Row>
      <Row label="Secondary color">
        <Input
          type="color"
          value={String(draft.secondaryColor ?? '#4B5563')}
          onChange={(e) => update('secondaryColor', e.target.value)}
          className="w-20"
        />
      </Row>
      <Row label="Body font">
        <FontSelect
          value={String(draft.bodyFont ?? 'Inter')}
          onChange={(v) => update('bodyFont', v)}
        />
      </Row>
      <Row label="Heading font">
        <FontSelect
          value={String(draft.headingFont ?? 'Inter')}
          onChange={(v) => update('headingFont', v)}
        />
      </Row>
      <Row label="Logo URL (light)">
        <Input
          value={String(draft.logoLightUrl ?? '')}
          onChange={(e) => update('logoLightUrl', e.target.value)}
        />
      </Row>
      <Row label="Logo URL (dark)">
        <Input
          value={String(draft.logoDarkUrl ?? '')}
          onChange={(e) => update('logoDarkUrl', e.target.value)}
        />
      </Row>
      <Row label="Cover image URL">
        <Input
          value={String(draft.coverImageUrl ?? '')}
          onChange={(e) => update('coverImageUrl', e.target.value)}
        />
      </Row>
      <Row label="Contact block (HTML)">
        <Textarea
          value={String(draft.contactBlock ?? '')}
          onChange={(e) => update('contactBlock', e.target.value)}
          rows={3}
        />
      </Row>
    </>
  );
}

function FontSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const FONTS = ['Inter', 'Source Sans', 'Lora', 'Merriweather', 'IBM Plex Sans'];
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {FONTS.map((f) => (
          <SelectItem key={f} value={f}>
            {f}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Workflow toggles that survived the doc-editor redo. The field-era
 * requirements ("Require photos in Findings", "Require caption on every
 * photo") were enforced by typed-field validation that no longer exists —
 * the free-form document model has no Findings preset or per-field photo
 * slots, so those toggles are gone from the UI.
 */
function WorkflowForm({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  const toggle = (key: string, label: string) => (
    <Row label={label}>
      <input
        type="checkbox"
        checked={Boolean(draft[key])}
        onChange={(e) => update(key, e.target.checked)}
      />
    </Row>
  );
  return (
    <>
      {toggle('allowMultiTechEditing', 'Allow multiple techs to edit')}
      {toggle('voiceToTextEnabled', 'Voice-to-text enabled')}
      {toggle('aiCaptionsEnabled', 'AI caption suggestions')}
      {toggle('aiSummaryOnPublish', 'AI summary on publish')}
    </>
  );
}

/**
 * A non-negative whole number of days. An empty field falls back to 0 (the safe
 * "off") — critical for the retention window, which drives destructive purges.
 */
function wholeDays(value: number | null): number {
  return value === null || !Number.isFinite(value) ? 0 : Math.max(0, Math.floor(value));
}

function SharingForm({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  return (
    <>
      <Row label="Default access mode">
        <Select
          value={String(draft.accessMode ?? 'PUBLIC_LINK')}
          onValueChange={(v) => update('accessMode', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PUBLIC_LINK">Public link</SelectItem>
            <SelectItem value="EMAIL_GATED">Email gated</SelectItem>
            <SelectItem value="PORTAL_ONLY">Portal only</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Expiry (days)">
        <NumberInput
          value={toNumberValue(draft.expiryDays)}
          allowDecimal={false}
          placeholder="365"
          onChange={(next) => update('expiryDays', next)}
        />
      </Row>
      <Row label="Allow PDF download">
        <input
          type="checkbox"
          checked={Boolean(draft.allowCustomerDownloadPdf ?? true)}
          onChange={(e) => update('allowCustomerDownloadPdf', e.target.checked)}
        />
      </Row>
      <Row label="Send on publish (email)">
        <input
          type="checkbox"
          checked={Boolean(draft.sendOnPublishEmail ?? true)}
          onChange={(e) => update('sendOnPublishEmail', e.target.checked)}
        />
      </Row>
      <Row label="Send on publish (SMS)">
        <input
          type="checkbox"
          checked={Boolean(draft.sendOnPublishSms ?? false)}
          onChange={(e) => update('sendOnPublishSms', e.target.checked)}
        />
      </Row>
      <Row label="Unopened reminder (days)">
        <div className="space-y-1">
          <NumberInput
            min={0}
            value={toNumberValue(draft.unopenedReminderDays)}
            placeholder="0"
            emptyValue={0}
            onChange={(next) => update('unopenedReminderDays', wholeDays(next))}
          />
          <p className="text-xs text-muted-foreground">
            Email the customer once if a published report goes unopened for this many days. 0
            disables reminders.
          </p>
        </div>
      </Row>
      <Row label="Retention (days)">
        <div className="space-y-1">
          <NumberInput
            min={0}
            value={toNumberValue(draft.retentionDays)}
            placeholder="0"
            emptyValue={0}
            onChange={(next) => update('retentionDays', wholeDays(next))}
          />
          <p className="text-xs text-muted-foreground">
            Permanently remove archived reports this many days after they were archived. 0 keeps
            archived reports indefinitely (recommended).
          </p>
        </div>
      </Row>
      <Row label="Custom domain (v1.1)">
        <Input
          value={String(draft.customDomain ?? '')}
          onChange={(e) => update('customDomain', e.target.value)}
          placeholder="reports.yourcompany.com"
        />
      </Row>
    </>
  );
}

function SignaturesForm({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  return (
    <>
      <Row label="Allow tech signatures">
        <input
          type="checkbox"
          checked={Boolean(draft.allowTechSignatures ?? false)}
          onChange={(e) => update('allowTechSignatures', e.target.checked)}
        />
      </Row>
      <Row label="Allow customer signatures">
        <input
          type="checkbox"
          checked={Boolean(draft.allowCustomerSignatures ?? true)}
          onChange={(e) => update('allowCustomerSignatures', e.target.checked)}
        />
      </Row>
      <Row label="Lock report on signature">
        <input
          type="checkbox"
          checked={Boolean(draft.lockOnSignature ?? true)}
          onChange={(e) => update('lockOnSignature', e.target.checked)}
        />
      </Row>
    </>
  );
}
